import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, Play, CheckCircle } from "lucide-react";

const DEMO_STEPS = [
  {
    id: 1,
    title: "Welcome to RUDRIK",
    description: "India ka first AML Compliance Intelligence Platform. Yeh demo aapko poora tool dikhayega — step by step.",
    targetPath: "/dashboard",
    targetSelector: '[data-testid="dashboard-stats"]',
    position: "center",
    highlight: null,
  },
  {
    id: 2,
    title: "Dashboard — Compliance Overview",
    description: "Yahan aap dekhte hain: total customers, active cases, risk distribution, SLA compliance, aur regulatory calendar — sab ek jagah.",
    targetPath: "/dashboard",
    targetSelector: '[data-testid="sla-compliance-section"]',
    position: "bottom",
    highlight: '[data-testid="sla-widget"]',
  },
  {
    id: 3,
    title: "Screening Hub — Real-time Screening",
    description: "Kisi bhi entity ko 15+ Indian databases ke against screen karein. Individual, Corporate, ya Director — sab supported hai. PAN, Aadhaar, GST, CIN sab optional fields available hain.",
    targetPath: "/screening",
    targetSelector: '[data-testid="screening-mode-tabs"]',
    position: "bottom",
    highlight: '[data-testid="screening-mode-tabs"]',
  },
  {
    id: 4,
    title: "Bulk Screening — CSV Upload",
    description: "100+ entities ek saath screen karein. CSV upload karein, 'Screen All' dabayein, results Excel mein download karein. Poora batch 30 seconds mein.",
    targetPath: "/screening",
    targetSelector: '[data-testid="tab-bulk"]',
    position: "bottom",
    highlight: '[data-testid="tab-bulk"]',
  },
  {
    id: 5,
    title: "Entity Intelligence — Risk Graph",
    description: "Kisi bhi entity ka poora network dekhein — directors, companies, court cases, ED investigations. Visual graph se hidden connections turant samajh aayein.",
    targetPath: "/entity-intelligence",
    targetSelector: '[data-testid="entity-search-input"]',
    position: "bottom",
    highlight: '[data-testid="entity-search-input"]',
  },
  {
    id: 6,
    title: "Transaction Monitoring",
    description: "6 pre-built rules — ₹10L+ cash, structuring, round-trip, spike detection. CSV upload karein, rules automatically alerts generate karein. Har alert assignable hai.",
    targetPath: "/transactions",
    targetSelector: '[data-testid="rules-panel"]',
    position: "bottom",
    highlight: '[data-testid="rules-panel"]',
  },
  {
    id: 7,
    title: "STR Generator — AI Powered",
    description: "FIU-IND compliant Suspicious Transaction Report 2 minute mein. AI narrative auto-generate hota hai. Draft → Review → File workflow. PDF export with RUDRIK branding.",
    targetPath: "/str-generator",
    targetSelector: '[data-testid="str-form"]',
    position: "bottom",
    highlight: '[data-testid="str-form"]',
  },
  {
    id: 8,
    title: "Reports Module",
    description: "6 pre-built compliance reports: Monthly AML, High Risk, PEP Exposure, Sanctions, STR Activity, CDD Completion. Date range select karein, generate karein, PDF download karein.",
    targetPath: "/reports",
    targetSelector: '[data-testid="reports-page"]',
    position: "bottom",
    highlight: null,
  },
  {
    id: 9,
    title: "Settings — India Data Sources",
    description: "10 Indian regulatory databases — MHA, RBI, SEBI, ED, CBI, MCA21, SFIO, NCLT, eCourts, GSTN. Toggle on/off, sync anytime, records count dekhein.",
    targetPath: "/settings",
    targetSelector: '[data-testid="tab-data_sources"]',
    position: "bottom",
    highlight: '[data-testid="tab-data_sources"]',
  },
  {
    id: 10,
    title: "Demo Complete!",
    description: "Aapne RUDRIK ka poora platform dekh liya. Ready to make your compliance RBI-proof? Book a live demo with your data.",
    targetPath: null,
    targetSelector: null,
    position: "center",
    highlight: null,
  },
];

export function DemoMode() {
  const [active, setActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showStartBtn, setShowStartBtn] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if demo was dismissed this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem("rudrik_demo_dismissed");
    if (dismissed) setShowStartBtn(false);
  }, []);

  const startDemo = () => {
    setActive(true);
    setCurrentStep(0);
    setShowStartBtn(false);
    const step = DEMO_STEPS[0];
    if (step.targetPath && location.pathname !== step.targetPath) {
      navigate(step.targetPath);
    }
  };

  const nextStep = () => {
    const next = currentStep + 1;
    if (next >= DEMO_STEPS.length) {
      endDemo();
      return;
    }
    setCurrentStep(next);
    const step = DEMO_STEPS[next];
    if (step.targetPath && location.pathname !== step.targetPath) {
      navigate(step.targetPath);
    }
  };

  const prevStep = () => {
    if (currentStep <= 0) return;
    const prev = currentStep - 1;
    setCurrentStep(prev);
    const step = DEMO_STEPS[prev];
    if (step.targetPath && location.pathname !== step.targetPath) {
      navigate(step.targetPath);
    }
  };

  const endDemo = () => {
    setActive(false);
    setCurrentStep(0);
    sessionStorage.setItem("rudrik_demo_dismissed", "true");
  };

  const step = DEMO_STEPS[currentStep];
  const progress = ((currentStep + 1) / DEMO_STEPS.length) * 100;

  // Highlight target element
  useEffect(() => {
    if (!active || !step?.highlight) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(step.highlight);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.outline = "2px solid #2563eb";
        el.style.outlineOffset = "4px";
        el.style.borderRadius = "8px";
        el.style.transition = "outline 0.3s ease";
        return () => {
          el.style.outline = "";
          el.style.outlineOffset = "";
        };
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [active, currentStep, step]);

  // Remove highlight on step change
  useEffect(() => {
    if (!active) {
      document.querySelectorAll("[style*='outline: 2px solid']").forEach(el => {
        el.style.outline = "";
        el.style.outlineOffset = "";
      });
    }
  }, [active, currentStep]);

  if (!active && !showStartBtn) return null;

  // Start Demo Button
  if (!active && showStartBtn) {
    return (
      <button
        onClick={startDemo}
        data-testid="start-demo-btn"
        style={{
          position: "fixed", top: "80px", right: "24px", zIndex: 100,
          background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
          border: "none", borderRadius: "10px", padding: "10px 20px",
          cursor: "pointer", color: "#fff", fontSize: "13px", fontWeight: 600,
          display: "flex", alignItems: "center", gap: "8px",
          boxShadow: "0 8px 24px rgba(37,99,235,0.4)",
          animation: "pulse-demo 2s infinite",
        }}
      >
        <Play style={{ width: "14px", height: "14px" }} /> Start Demo
      </button>
    );
  }

  // Demo Overlay
  return (
    <>
      {/* Backdrop */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 90,
        background: "rgba(0,0,0,0.5)", backdropFilter: "blur(2px)",
        pointerEvents: step.position === "center" ? "auto" : "none",
      }} />

      {/* Tooltip Card */}
      <div data-testid="demo-tooltip" style={{
        position: "fixed", zIndex: 100,
        ...(step.position === "center"
          ? { top: "50%", left: "50%", transform: "translate(-50%, -50%)" }
          : { bottom: "24px", left: "50%", transform: "translateX(-50%)" }
        ),
        width: "440px", maxWidth: "90vw",
        background: "#0d1117", border: "1px solid #2563eb",
        borderRadius: "14px", padding: "24px",
        boxShadow: "0 16px 48px rgba(37,99,235,0.2)",
        animation: "fadeInUp 0.3s ease",
      }}>
        {/* Progress */}
        <div style={{ marginBottom: "16px" }}>
          <div className="flex items-center justify-between mb-2">
            <span style={{ fontSize: "10px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "1px" }}>
              Step {currentStep + 1} of {DEMO_STEPS.length}
            </span>
            <button onClick={endDemo} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#475569", padding: "2px" }}>
              <X style={{ width: "16px", height: "16px" }} />
            </button>
          </div>
          <div style={{ width: "100%", height: "3px", background: "#1e2530", borderRadius: "2px", overflow: "hidden" }}>
            <div style={{ width: `${progress}%`, height: "100%", background: "#2563eb", transition: "width 0.3s ease", borderRadius: "2px" }} />
          </div>
        </div>

        {/* Content */}
        <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#f1f5f9", marginBottom: "8px" }}>
          {step.id === 10 && <CheckCircle style={{ width: "18px", height: "18px", color: "#10b981", display: "inline", marginRight: "8px", verticalAlign: "middle" }} />}
          {step.title}
        </h3>
        <p style={{ fontSize: "13px", color: "#94a3b8", lineHeight: 1.7, marginBottom: "20px" }}>
          {step.description}
        </p>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            style={{
              background: "transparent", border: "1px solid #1e2530", borderRadius: "8px",
              padding: "8px 16px", cursor: currentStep === 0 ? "not-allowed" : "pointer",
              color: currentStep === 0 ? "#334155" : "#94a3b8", fontSize: "12px", fontWeight: 600,
              display: "flex", alignItems: "center", gap: "4px", opacity: currentStep === 0 ? 0.4 : 1,
            }}
          >
            <ArrowLeft style={{ width: "12px", height: "12px" }} /> Back
          </button>

          {step.id === 10 ? (
            <div className="flex gap-2">
              <button onClick={endDemo} style={{
                background: "transparent", border: "1px solid #1e2530", borderRadius: "8px",
                padding: "8px 16px", cursor: "pointer", color: "#94a3b8", fontSize: "12px", fontWeight: 600,
              }}>
                Close Demo
              </button>
              <a href="mailto:contact@rudrik.io" style={{
                background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: "8px",
                padding: "8px 20px", color: "#fff", fontSize: "12px", fontWeight: 600,
                display: "inline-flex", alignItems: "center", gap: "6px", textDecoration: "none",
              }}>
                Book Full Demo <ArrowRight style={{ width: "12px", height: "12px" }} />
              </a>
            </div>
          ) : (
            <button onClick={nextStep} data-testid="demo-next-btn" style={{
              background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: "8px",
              padding: "8px 20px", cursor: "pointer", color: "#fff", fontSize: "12px", fontWeight: 600,
              display: "flex", alignItems: "center", gap: "6px",
            }}>
              Next <ArrowRight style={{ width: "12px", height: "12px" }} />
            </button>
          )}
        </div>
      </div>
    </>
  );
}
